import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPinnedMessages,
  pinChatMessage,
  unpinChatMessage,
} from "controller/api/chat/pinChat";
import { queryKeys } from "controller/cache/helpers/queryKeyGenerator";
import type {
  GetPinnedMessagesInput,
  PinMessageMutationInput,
  PinnedMessageItem,
  UnpinMessageMutationInput,
} from "shared/types/chat";

export const useGetPinnedMessages = ({
  serviceId,
  type,
}: GetPinnedMessagesInput) => {
  return useQuery({
    queryKey: queryKeys.pinnedMessages.list(serviceId),
    queryFn: () => getPinnedMessages({ serviceId, type }),
    enabled: Boolean(serviceId),
  });
};

export const usePinMessage = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ chatId, type, excerpt }: PinMessageMutationInput) =>
      pinChatMessage({ chatId, type, excerpt }),
    {
      async onMutate(variables) {
        const key = queryKeys.pinnedMessages.list(variables.serviceId);
        await queryClient.cancelQueries({ queryKey: key });

        const previous = queryClient.getQueryData<PinnedMessageItem[]>(key);

        const optimisticItem: PinnedMessageItem = {
          chat_id: variables.chatId,
          service_id: variables.serviceId,
          excerpt: variables.excerpt,
          chat: variables.message,
        };

        queryClient.setQueryData<PinnedMessageItem[]>(key, (old) => [
          optimisticItem,
          ...(old ?? []),
        ]);

        return { previous, key };
      },
      onError(_error, _variables, context) {
        if (context?.previous !== undefined) {
          queryClient.setQueryData(context.key, context.previous);
        }
      },
      onSettled(_data, _error, variables) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.pinnedMessages.list(variables.serviceId),
        });
      },
    },
  );
};

export const useUnpinMessage = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ chatId, type }: UnpinMessageMutationInput) =>
      unpinChatMessage({ chatId, type }),
    {
      async onMutate(variables) {
        const key = queryKeys.pinnedMessages.list(variables.serviceId);
        await queryClient.cancelQueries({ queryKey: key });

        const previous = queryClient.getQueryData<PinnedMessageItem[]>(key);

        queryClient.setQueryData<PinnedMessageItem[]>(key, (old) =>
          (old ?? []).filter((item) => item.chat_id !== variables.chatId),
        );

        return { previous, key };
      },
      onError(_error, _variables, context) {
        if (context?.previous !== undefined) {
          queryClient.setQueryData(context.key, context.previous);
        }
      },
      onSettled(_data, _error, variables) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.pinnedMessages.list(variables.serviceId),
        });
      },
    },
  );
};
